import {
  entryToIndication,
  rautReportCustomInfoCast,
  RautReportIndication,
} from 'features/indicationTriage'
import { useRolodexEntries } from 'features/rolodex'
import {
  extractScanNumber,
  sortIndicationsList,
} from 'pages/reporting/reports/raut/helpers/indications'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

/**
 * Return type for the useIndications hook
 */
interface UseIndicationsResult {
  indicationsByInspectionId: ReadonlyMap<string, RautReportIndication[]>
  flatIndications: RautReportIndication[]
  isLoading: boolean
}

/**
 * Hook to manage indication data for report generation
 *
 * Provides:
 * - List of all indications
 * - Sorted list of indications (by numeric suffix)
 * - Loading and error states
 *
 * @param indicationSetId - ID of the current indication-set
 * @returns {UseIndicationsResult} Processed indication data and states
 */
export const useIndications = (
  indicationSetIds: string[] | null,
  inspectionIds: string[] | null
): UseIndicationsResult => {
  const { entries, isLoading } = useRolodexEntries(
    indicationSetIds && indicationSetIds.length > 0
      ? [
          {
            type: RolodexEntryType.INDICATION,
            tags: {
              'indication-set': indicationSetIds,
            },
          },
        ]
      : undefined
  )

  const rolodexIndications = useMemo(() => {
    return entries
      .filter((entry) => entry.type === RolodexEntryType.INDICATION)
      .map((entry) => entryToIndication(entry))
  }, [entries])

  const indicationsByInspectionId = useMemo(() => {
    const map = new Map<string, RautReportIndication[]>()

    if (!inspectionIds) return map

    inspectionIds.forEach((id) => map.set(id, []))

    rolodexIndications.forEach((indication) => {
      const inspectionTag = indication.tags.inspection
      if (inspectionTag) {
        if (inspectionTag && map.has(inspectionTag)) {
          const customInfo = rautReportCustomInfoCast(indication.customInfo)
          if (customInfo) {
            const { ...indicationItem } = indication
            map.get(inspectionTag)!.push({
              ...indicationItem,
              customInfo,
            } as RautReportIndication)
          }
        }
      }
    })

    map.keys().forEach((key) => {
      map.set(
        key,
        sortIndicationsList(map.get(key) as RautReportIndication[]).map(
          (indication) => ({
            ...indication,
            title: `S${extractScanNumber(indication.id)}`,
          })
        )
      )
    })

    return map as ReadonlyMap<string, RautReportIndication[]>
  }, [inspectionIds, rolodexIndications])

  const flatIndications = useMemo(() => {
    return Array.from(indicationsByInspectionId.values()).flat()
  }, [indicationsByInspectionId])

  return {
    indicationsByInspectionId,
    flatIndications,
    isLoading,
  }
}
